import React, { useState, useEffect, useCallback, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Edit, Mail, Phone, Globe, MapPin, Calendar, MoreVertical, AlertCircle, Users, Languages, UserCog, Loader2, CheckCircle, XCircle, PhoneOff, MessageCircleOff, MessageCircle, Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useGoBack } from "@/hooks/useGoBack";
import { createPageUrl } from "@/utils";
import { useContact, useContacts } from "@/hooks/useContacts";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Skeleton } from "@/components/ui/skeleton";
import { useTwilio } from "@/contexts/TwilioContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";

import { toast } from "sonner";
import { getAvatarColor, formatPhoneNumber, getFullPhoneE164 } from "@/lib/utils";
import { getStateName, getCountryName } from "@/lib/locationUtils";

import ContactForm from "../components/contacts/ContactForm";
import DocumentsTab from "../components/contacts/DocumentsTab";
import { ContactActivityTimeline } from "../components/contacts/ContactActivityTimeline";
import DealsTab from "../components/contacts/DealsTab";
import CasesServicesTab from "../components/contacts/CasesServicesTab";
import { ResponsiveTabGroup } from "@/components/common/ResponsiveTabGroup";
import { StatusBadge } from "@/components/common/StatusBadge";
import { ContactStatusBadges } from "../components/contacts/ContactStatusBadges";
import { FamilyRelationshipsTab } from "../components/contacts/FamilyRelationshipsTab";
import { ReferralsTab } from "../components/contacts/ReferralsTab";
import { FinanceiroTab } from "../components/contacts/FinanceiroTab";
import { useReferrals } from "@/hooks/useReferrals";
import { ContactInsightsPanel } from "../components/contacts/ContactInsightsPanel";
import { ReactivationCard } from "../components/contacts/ReactivationCard";
import { SOURCE_TYPES, CHANNEL_TYPES } from "@/lib/sourceChannelTypes";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ScheduleCallModal } from "@/components/calls/ScheduleCallModal";
import DealForm from "@/components/deals/DealForm";
import { ChatCore, DealChatHeader, DrawerChatLayout } from "@/features/chat";
import { useTasks } from "@/hooks/useTasks";
import { useForm } from "react-hook-form";
import { useDeals } from "@/hooks/useDeals";
import { useTranslation } from "react-i18next";
import { useUserPermissions } from "@/hooks/useUserPermissions";

const statusColors = {
  active: "bg-emerald-100 text-emerald-700 border-emerald-200",
  inactive: "bg-slate-100 text-slate-700 border-slate-200",
  completed: "bg-slate-100 text-slate-700 border-slate-200",
  dormant: "bg-amber-100 text-amber-700 border-amber-200",
  prospecting: "bg-purple-100 text-purple-700 border-purple-200",
  churned: "bg-rose-100 text-rose-700 border-rose-200"
};

const formatDate = (dateValue) => {
  if (!dateValue) return "N/A";

  try {
    let date;

    if (dateValue instanceof Date) {
      date = dateValue;
    } else if (typeof dateValue === 'string') {
      date = new Date(dateValue);
    } else if (typeof dateValue === 'number') {
      date = new Date(dateValue);
    } else {
      console.warn("Unsupported date type:", typeof dateValue, dateValue);
      return "N/A";
    }

    if (isNaN(date.getTime())) {
      console.warn("Invalid date:", dateValue);
      return "N/A";
    }

    return format(date, "d 'de' MMMM 'de' yyyy", { locale: ptBR });
  } catch (error) {
    console.error("Error formatting date:", error, dateValue);
    return "N/A";
  }
};

export default function ContactDetail() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const goBack = useGoBack('/contacts');
  const [activeTab, setActiveTab] = useState("overview");
  const [isEditing, setIsEditing] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  // showAssignDialog removido — atribuição vive no deal
  void useState;
  
  const [meetingsCount, setMeetingsCount] = useState(0);
  const [primaryAddress, setPrimaryAddress] = useState(null);
  const [addressLoading, setAddressLoading] = useState(false);
  const [showDealForm, setShowDealForm] = useState(false);
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [showScheduleCall, setShowScheduleCall] = useState(false);
  const [contactDeals, setContactDeals] = useState([]);
  const [selectedDealsToDelete, setSelectedDealsToDelete] = useState([]);
  const [loadingDeals, setLoadingDeals] = useState(false);
  const [isCreatingDeal, setIsCreatingDeal] = useState(false);
  const [showWhatsAppSheet, setShowWhatsAppSheet] = useState(false);
  
  const { makeCall, isReady, isInitiatingCall } = useTwilio();
  const { deleteContact } = useContacts(false, { enabled: false });
  const { createTask } = useTasks();
  const { createDeal } = useDeals({ enabled: false }); // Disable auto-fetch - only use createDeal
  const { register, handleSubmit: handleTaskSubmit, reset: resetTask, setValue: setTaskValue } = useForm();

  const urlParams = new URLSearchParams(window.location.search);
  const contactId = urlParams.get('id');

  const { contact, loading: isLoading, error, loadContact } = useContact(contactId);
  const { isAdmin } = useUserPermissions();
  const { referredBy } = useReferrals(contactId);
  const [activeDealId, setActiveDealId] = useState(null);
  const [unreadWhatsAppCount, setUnreadWhatsAppCount] = useState(0);


  // Função para carregar endereço primário do contato
  const loadAddress = useCallback(async () => {
    if (!contact?.id) {
      setPrimaryAddress(null);
      return;
    }
    try {
      setAddressLoading(true);
      let addr = null;
      if (contact.primary_address) {
        const { data, error } = await supabase
          .from('contact_addresses')
          .select('*')
          .eq('id', contact.primary_address)
          .maybeSingle();
        if (error) throw error;
        addr = data;
      } else {
        // fallback: tenta primário; se não houver, pega o mais recente
        let data = null;
        let error = null;
        let res = await supabase
          .from('contact_addresses')
          .select('*')
          .eq('contact_id', contact.id)
          .eq('is_primary', true)
          .maybeSingle();
        data = res.data; error = res.error;
        if (error) throw error;
        if (!data) {
          const res2 = await supabase
            .from('contact_addresses')
            .select('*')
            .eq('contact_id', contact.id)
            .order('is_primary', { ascending: false })
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();
          data = res2.data;
        }
        addr = data || null;
      }
      setPrimaryAddress(addr);
    } catch (e) {
      console.error('Erro ao carregar endereço:', e);
      setPrimaryAddress(null);
    } finally {
      setAddressLoading(false);
    }
  }, [contact?.id, contact?.primary_address]);

  // Carrega endereço quando o contato muda
  useEffect(() => {
    loadAddress();
  }, [loadAddress]);


  // Load active deal for contact (to pass to ScheduleCallModal)
  useEffect(() => {
    const fetchActiveDeal = async () => {
      if (!contactId) {
        setActiveDealId(null);
        return;
      }
      
      try {
        const { data, error } = await supabase
          .from('deals')
          .select('id')
          .eq('contact_id', contactId)
          .not('stage', 'in', '("won","lost")')
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        
        if (error) {
          console.error('Error fetching active deal:', error);
          setActiveDealId(null);
        } else {
          setActiveDealId(data?.id || null);
        }
      } catch (err) {
        console.error('Error fetching active deal:', err);
        setActiveDealId(null);
      }
    };
    
    fetchActiveDeal();
  }, [contactId]);

  // Fetch unread WhatsApp messages count
  useEffect(() => {
    if (!contactId) return;

    const fetchUnreadCount = async () => {
      try {
        const { count, error } = await supabase
          .from('messages')
          .select('id', { count: 'exact', head: true })
          .eq('contact_id', contactId)
          .eq('direction', 'received')
          .is('read_at', null);

        if (!error && count !== null) {
          setUnreadWhatsAppCount(count);
        }
      } catch (err) {
        console.error('Error fetching unread count:', err);
      }
    };

    fetchUnreadCount();

    // Poll for new messages every 5 seconds
    const interval = setInterval(fetchUnreadCount, 5000);

    // Subscribe to real-time updates
    const channel = supabase
      .channel('messages-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'messages',
          filter: `contact_id=eq.${contactId}`
        },
        () => {
          fetchUnreadCount();
        }
      )
      .subscribe();

    return () => {
      clearInterval(interval);
      supabase.removeChannel(channel);
    };
  }, [contactId]);


  const handleCallContact = async () => {
    console.log('handleCallContact chamado', { 
      phone: contact?.primary_phone, 
      contactId: contact?.id,
      isReady 
    });

    if (!contact?.primary_phone) {
      console.error('Contato sem telefone');
      toast.error('Contato não possui telefone cadastrado');
      return;
    }

    if (!isReady) {
      console.error('Device não está pronto');
      toast.error('Dispositivo de chamada não está pronto. Aguarde...');
      return;
    }

    try {
      const phoneE164 = getFullPhoneE164(contact.primary_phone, contact.primary_phone_country_code);
      console.log('Tentando fazer ligação para:', phoneE164);
      if (window.innerWidth >= 768) {
        toast.info('Iniciando chamada...');
      }
      await makeCall(phoneE164, contact.id);
      console.log('makeCall executado com sucesso');
    } catch (error) {
      console.error('Erro ao iniciar chamada:', error);
      toast.error('Erro ao iniciar chamada: ' + error.message);
    }
  };

  const handleCallBack = async (phoneNumber) => {
    if (!isReady) {
      toast.error('Dispositivo de chamada não está pronto. Aguarde...');
      return;
    }

    try {
      if (window.innerWidth >= 768) {
        toast.info('Retornando ligação...');
      }
      await makeCall(phoneNumber, contact.id);
    } catch (error) {
      console.error('Erro ao retornar chamada:', error);
      toast.error('Erro ao retornar chamada');
    }
  };

  const handleUpdateContact = async (contactData) => {
    setIsProcessing(true);
    try {
      let addressId = contactData.primary_address;
      
      // Verifica se há qualquer campo de endereço preenchido
      const hasAddressData = addressId && typeof addressId === 'object' && 
        (addressId.line1 || addressId.city || addressId.state || addressId.postal_code);

      // Se primary_address for um objeto com dados, trata o endereço
      if (hasAddressData) {
        // Se já existe um UUID no contato, atualiza o endereço existente
        if (contact.primary_address && typeof contact.primary_address === 'string') {
          const { error: addressUpdateError } = await supabase
            .from('contact_addresses')
            .update({
              type: addressId.type || 'home',
              line1: addressId.line1 || null,
              line2: addressId.line2 || null,
              city: addressId.city || null,
              state: addressId.state || null,
              postal_code: addressId.postal_code || null,
              country_code: addressId.country_code || null
            })
            .eq('id', contact.primary_address);
          
          if (addressUpdateError) {
            console.error('Error updating address:', addressUpdateError);
            throw addressUpdateError;
          }
          addressId = contact.primary_address;
        } else {
          // Senão, cria um novo endereço
          const { data: newAddress, error: addressError } = await supabase
            .from('contact_addresses')
            .insert([{
              contact_id: contactId,
              type: addressId.type || 'home',
              line1: addressId.line1 || null,
              line2: addressId.line2 || null,
              city: addressId.city || null,
              state: addressId.state || null,
              postal_code: addressId.postal_code || null,
              country_code: addressId.country_code || null,
              is_primary: true
            }])
            .select()
            .single();
          
          if (addressError) {
            console.error('Error creating address:', addressError);
            throw addressError;
          }
          addressId = newAddress.id;
        }
      } else if (contact.primary_address && typeof contact.primary_address === 'string') {
        // Usuário limpou todos os campos - remove o endereço existente
        const { error: deleteError } = await supabase
          .from('contact_addresses')
          .delete()
          .eq('id', contact.primary_address);
        
        if (deleteError) {
          console.error('Error deleting address:', deleteError);
          throw deleteError;
        }
        addressId = null;
      }
      
      // Prepara os dados do contato com o UUID do endereço (ou null)
      const updateData = {
        ...contactData,
        primary_address: addressId && typeof addressId === 'string' ? addressId : null
      };

      const { error } = await supabase
        .from('contacts')
        .update(updateData)
        .eq('id', contactId);

      if (error) throw error;
      
      await loadContact();
      await loadAddress();
      setIsEditing(false);
      toast.success('Contact updated successfully');
    } catch (error) {
      console.error("Error updating contact:", error);
      
      // Check if it's a duplicate phone error
      if (error.message?.includes('DUPLICATE_PHONE::') || error.message?.includes('DUPLICATE_DETECTED::')) {
        const parts = error.message.split('::');
        const isDuplicatePhone = parts[0] === 'DUPLICATE_PHONE';
        const duplicateId = isDuplicatePhone ? parts[1] : null;
        const message = isDuplicatePhone ? parts[2] : parts[1];
        
        toast.error(message, {
          duration: 10000,
          action: {
            label: 'Ir para Merge',
            onClick: () => navigate(createPageUrl('MergeContacts'))
          }
        });
      } else {
        toast.error('Error updating contact: ' + error.message);
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const loadContactDeals = async () => {
    setLoadingDeals(true);
    try {
      const { data, error } = await supabase
        .from('deals')
        .select('id, name, stage, total_amount, currency')
        .eq('contact_id', contactId);
      
      if (error) throw error;
      setContactDeals(data || []);
    } catch (error) {
      console.error("Error loading deals:", error);
      setContactDeals([]);
    }
    setLoadingDeals(false);
  };

  const handleDeleteContact = async () => {
    try {
      // Primeiro: deletar calls vinculadas aos deals selecionados
      if (selectedDealsToDelete.length > 0) {
        const { error: deleteCallsError } = await supabase
          .from('calls')
          .delete()
          .in('deal_id', selectedDealsToDelete);
        
        if (deleteCallsError) {
          console.error('Error deleting calls linked to deals:', deleteCallsError);
          throw deleteCallsError;
        }
        
        // Depois: deletar os deals
        const { error: deleteDealsError } = await supabase
          .from('deals')
          .delete()
          .in('id', selectedDealsToDelete);
        
        if (deleteDealsError) throw deleteDealsError;
      }
      
      await deleteContact(contactId);
      toast.success("Contact deleted successfully");
      navigate(createPageUrl("Contacts"));
    } catch (error) {
      toast.error("Error deleting contact: " + error.message);
    }
    setShowDeleteDialog(false);
  };

  const handleCreateTask = async (data) => {
    try {
      await createTask.mutateAsync({
        ...data,
        status: 'pending',
        related_to_type: 'contact',
        related_to_id: contactId,
      });
      setShowTaskForm(false);
      resetTask();
      toast.success('Task created successfully');
    } catch (error) {
      console.error('Error creating task:', error);
      toast.error('Error creating task');
    }
  };

  const dealSubmitLockRef = useRef(false);
  const handleCreateDeal = async (dealData, catalogId) => {
    if (dealSubmitLockRef.current || isCreatingDeal) {
      return;
    }
    dealSubmitLockRef.current = true;
    setIsCreatingDeal(true);
    try {
      const newDeal = await createDeal({
        ...dealData,
        contact_id: contactId,
      });

      // Se selecionou processo/serviço, criar item separadamente
      if (catalogId && newDeal?.id) {
        const { data: catalogItem } = await supabase
          .from('catalog')
          .select('*')
          .eq('id', catalogId)
          .single();

        if (catalogItem) {
          const price = Number(catalogItem.base_price) || 0;
          await supabase.from('items').insert([{
            deal_id: newDeal.id,
            catalog_id: catalogId,
            quantity: 1,
            unit_price: price,
            subtotal: price,
            total_amount: price,
            status: 'proposed'
          }]);
        }
      }

      setShowDealForm(false);
      toast.success('Negócio criado com sucesso');
    } catch (error) {
      console.error('Error creating deal:', error);
      toast.error('Erro ao criar negócio');
    } finally {
      setIsCreatingDeal(false);
      dealSubmitLockRef.current = false;
    }
  };

  const getStatusColor = (status) => {
    return statusColors[status] || "bg-slate-100 text-slate-700 border-slate-200";
  };

  const getFullName = () => {
    if (!contact) return 'Unknown Contact';
    if (contact.first_name && contact.last_name) {
      return `${contact.first_name} ${contact.last_name}`;
    }
    return contact.full_name || 'Unknown Contact';
  };

  const getInitials = () => {
    if (!contact) return 'C';
    if (contact.first_name && contact.last_name) {
      return `${contact.first_name.charAt(0)}${contact.last_name.charAt(0)}`.toUpperCase();
    }
    if (contact.full_name) {
      return contact.full_name.charAt(0).toUpperCase();
    }
    return 'C';
  };

  if (!contactId) {
    navigate(createPageUrl("Contacts"));
    return null;
  }

  if (isLoading) {
    return (
      <div className="bg-slate-50 dark:bg-slate-900 p-3 sm:p-4 md:p-8 overflow-x-hidden">
        <div className="max-w-6xl mx-auto space-y-6 md:space-y-8">
          <Skeleton className="h-8 md:h-10 w-48 md:w-64" />
          <div className="flex items-center gap-3">
            <Skeleton className="h-12 w-12 md:h-16 md:w-16 rounded-full flex-shrink-0" />
            <div className="space-y-2 min-w-0 flex-1">
              <Skeleton className="h-6 md:h-8 w-full max-w-48" />
              <Skeleton className="h-4 w-24" />
            </div>
          </div>
          <div className="flex gap-2 mb-6 md:mb-8 bg-slate-100 dark:bg-slate-800 p-1.5 rounded-full inline-flex overflow-x-auto scrollbar-hide">
            <Skeleton className="h-8 md:h-10 w-20 md:w-24 rounded-full flex-shrink-0" />
            <Skeleton className="h-8 md:h-10 w-20 md:w-24 rounded-full flex-shrink-0" />
            <Skeleton className="h-8 md:h-10 w-20 md:w-24 rounded-full flex-shrink-0" />
          </div>
          <Skeleton className="h-72 md:h-96 w-full" />
        </div>
      </div>
    );
  }

  if (error || !contact) {
    return (
      <div className="bg-slate-50 dark:bg-slate-900 p-3 sm:p-4 md:p-8 overflow-x-hidden">
        <div className="max-w-6xl mx-auto">
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {error?.message || t('contacts.contactNotFound')}
            </AlertDescription>
          </Alert>
          <Button onClick={() => navigate(createPageUrl("Contacts"))} className="mt-4">
            {t('contacts.backToContacts')}
          </Button>
        </div>
      </div>
    );
  }

  if (isEditing) {
    return (
      <div className="bg-slate-50 dark:bg-slate-900 p-3 sm:p-4 md:p-8 overflow-x-hidden">
        <div className="max-w-3xl mx-auto">
          <Button
            variant="outline"
            onClick={() => setIsEditing(false)}
            className="mb-4 md:mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('common.cancel')}
          </Button>
          <Dialog open={true} onOpenChange={() => setIsEditing(false)}>
            <DialogContent className="w-[calc(100vw-1.5rem)] sm:w-[calc(100vw-2rem)] max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{t('contacts.editContact')}</DialogTitle>
              </DialogHeader>
              <ContactForm
                contact={{
                  ...contact,
                  primary_address: primaryAddress || contact.primary_address
                }}
                onSubmit={handleUpdateContact}
                onCancel={() => setIsEditing(false)}
                isProcessing={isProcessing}
                simplified={false}
              />
            </DialogContent>
          </Dialog>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-50 dark:bg-slate-900 p-3 sm:p-4 md:p-8 overflow-x-hidden">
      <div className="max-w-6xl mx-auto">
        <button
          onClick={goBack}
          className="mb-4 md:mb-6 -ml-1 md:-ml-2 text-slate-600 dark:text-slate-400 flex items-center gap-2 hover:scale-105 transition-transform duration-200 text-sm md:text-base"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('common.back')}
        </button>

        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4 mb-6 md:mb-8">
          <div className="flex items-center gap-3 min-w-0">
            <div className={`w-12 h-12 md:w-16 md:h-16 flex-shrink-0 bg-gradient-to-br ${getAvatarColor(contact?.first_name || contact?.full_name)} rounded-full flex items-center justify-center shadow-lg`}>
              <span className="text-white font-bold text-xl md:text-2xl">
                {getInitials()}
              </span>
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-900 dark:text-slate-100 mb-1 truncate">{getFullName()}</h1>
              <div className="flex flex-wrap items-center gap-1.5 md:gap-2">
                <ContactStatusBadges contact={contact} />
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-2 flex-shrink-0 self-end sm:self-start">
            {/* WhatsApp Quick Button */}
            {contact?.primary_phone && (
              <Button
                variant="outline"
                size="icon"
                className="rounded-full h-8 w-8 md:h-9 md:w-9 border-green-300 text-green-600 hover:bg-green-50 hover:text-green-700"
                onClick={() => setShowWhatsAppSheet(true)}
                title="Enviar WhatsApp"
              >
                <MessageCircle className="w-4 h-4 md:w-5 md:h-5" />
              </Button>
            )}
            {/* Quick Actions Button */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="default"
                  size="icon"
                  className="rounded-full h-8 w-8 md:h-9 md:w-9"
                >
                  <Plus className="w-4 h-4 md:w-5 md:h-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={() => setShowDealForm(true)}>
                  <span className="mr-2">💼</span>
                  {t('contacts.createDeal')}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setShowTaskForm(true)}>
                  <span className="mr-2">✅</span>
                  {t('contacts.createTask')}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setShowScheduleCall(true)}>
                  <span className="mr-2">📞</span>
                  {t('contacts.scheduleCall')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Existing More Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-slate-600 hover:text-slate-600 hover:bg-transparent transition-all duration-200 hover:scale-105 h-8 w-8 md:h-9 md:w-9"
                >
                  <MoreVertical className="w-4 h-4 md:w-5 md:h-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setIsEditing(true)}>
                  <Edit className="w-4 h-4 mr-2" />
                  {t('contacts.editContact')}
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => {
                    loadContactDeals();
                    setSelectedDealsToDelete([]);
                    setShowDeleteDialog(true);
                  }}
                  className="text-destructive focus:text-destructive"
                >
                  {t('contacts.deleteContact')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <div>
          <ResponsiveTabGroup
            tabs={[
              { id: 'overview', label: t('tabs.overview') },
              { id: 'insights', label: t('tabs.aiInsights') },
              { id: 'deals', label: t('tabs.deals') },
              { id: 'cases-services', label: t('tabs.casesServices') },
              { id: 'activities', label: 'Atividades' },
              { id: 'documents', label: t('tabs.documents') },
              { id: 'financeiro', label: t('tabs.financeiro', 'Financeiro') },
              { id: 'family', label: t('tabs.familyFriends') },
              { id: 'referrals', label: t('tabs.referrals') },
            ]}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            maxWidth={1200}
            className="mb-6 md:mb-8"
          />

          {activeTab === "overview" && (
            <div className="grid lg:grid-cols-3 gap-4 md:gap-8">
              <div className="lg:col-span-2 space-y-6 md:space-y-8">
                <div>
                  <h2 className="text-base md:text-lg font-bold text-slate-900 dark:text-slate-100 mb-4 md:mb-6">{t('contacts.contactInformation')}</h2>
                  <div className="grid md:grid-cols-2 gap-4 md:gap-8">
                    <div className="space-y-4">
                      <h3 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">{t('contacts.contact')}</h3>
                      
                      {/* Email - sempre mostrar */}
                      <div className="flex items-start gap-3 text-slate-700 dark:text-slate-300">
                        <Mail className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-xs text-slate-500 dark:text-slate-400">{t('common.email')}</p>
                          <p className="break-all font-medium">{contact.primary_email || ""}</p>
                        </div>
                      </div>
                      
                      {/* Phone - sempre mostrar */}
                      <div className="flex items-start gap-3 text-slate-700 dark:text-slate-300">
                        {contact.primary_phone ? (
                          <button
                            onClick={handleCallContact}
                            disabled={isInitiatingCall || !isReady}
                            className="p-1.5 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900 transition-colors group disabled:opacity-50 disabled:cursor-not-allowed relative"
                            title={isInitiatingCall ? t('calls.initiatingCall') : t('calls.callContact')}
                          >
                            {isInitiatingCall ? (
                              <div className="w-5 h-5 flex items-center justify-center">
                                <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                              </div>
                            ) : (
                              <Phone className="w-5 h-5 text-slate-400 group-hover:text-blue-600 flex-shrink-0" />
                            )}
                          </button>
                        ) : (
                          <Phone className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
                        )}
                        <div>
                          <p className="text-xs text-slate-500 dark:text-slate-400">{t('common.phone')}</p>
                  <p className="font-medium">
                    {contact.primary_phone 
                      ? formatPhoneNumber(contact.primary_phone, contact.primary_phone_country_code)
                      : ""}
                  </p>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <h3 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">{t('contacts.personalInfo')}</h3>
                      
                      {/* Preferred Name - sempre mostrar */}
                      <div className="flex items-start gap-3 text-slate-700 dark:text-slate-300">
                        <Users className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-xs text-slate-500 dark:text-slate-400">{t('fields.preferredName')}</p>
                          <p className="font-medium">{contact.preferred_name || ""}</p>
                        </div>
                      </div>
                      
                      {/* Gender - sempre mostrar */}
                      <div className="flex items-start gap-3 text-slate-700 dark:text-slate-300">
                        <Users className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-xs text-slate-500 dark:text-slate-400">{t('fields.gender')}</p>
                          <p className="font-medium capitalize">{contact.gender ? t(`genderOptions.${contact.gender}`, contact.gender) : ""}</p>
                        </div>
                      </div>
                      
                      {/* Date of Birth - sempre mostrar */}
                      <div className="flex items-start gap-3 text-slate-700 dark:text-slate-300">
                        <Calendar className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-xs text-slate-500 dark:text-slate-400">{t('fields.dateOfBirth')}</p>
                          <p className="font-medium">{contact.date_of_birth ? formatDate(contact.date_of_birth) : ""}</p>
                        </div>
                      </div>
                      
                      {/* Language - sempre mostrar */}
                      <div className="flex items-start gap-3 text-slate-700 dark:text-slate-300">
                        <Languages className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-xs text-slate-500 dark:text-slate-400">{t('fields.language')}</p>
                          <p className="font-medium capitalize">{contact.language ? t(`languageOptions.${contact.language}`, contact.language) : ""}</p>
                        </div>
                      </div>
                      
                      {/* Country of Birth - sempre mostrar */}
                      <div className="flex items-start gap-3 text-slate-700 dark:text-slate-300">
                        <Globe className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-xs text-slate-500 dark:text-slate-400">{t('fields.countryOfBirth')}</p>
                          <p className="font-medium">{contact.country_of_birth ? t(`countryOptions.${contact.country_of_birth.toLowerCase().replace(/\s+/g, '_')}`, contact.country_of_birth) : ""}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Address - sempre mostrar */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">{t('common.address')}</h3>
                  {addressLoading ? (
                    <div className="space-y-2">
                      <Skeleton className="h-4 w-2/3" />
                      <Skeleton className="h-4 w-1/2" />
                    </div>
                  ) : (
                    <div className="flex items-start gap-3 text-slate-700 dark:text-slate-300">
                      <MapPin className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
                      <div>
                        {primaryAddress ? (
                          <>
                            <p className="font-medium">
                              {[primaryAddress.line1, primaryAddress.line2].filter(Boolean).join(', ')}
                            </p>
                            <p className="text-sm text-slate-600 dark:text-slate-400">
                              {[
                                primaryAddress.city, 
                                getStateName(primaryAddress.state, primaryAddress.country_code), 
                                primaryAddress.postal_code, 
                                getCountryName(primaryAddress.country_code)
                              ].filter(Boolean).join(', ')}
                            </p>
                          </>
                        ) : contact.primary_address_line1 ? (
                          <>
                            <p className="font-medium">
                              {[contact.primary_address_line1, contact.primary_address_line2].filter(Boolean).join(', ')}
                            </p>
                            <p className="text-sm text-slate-600 dark:text-slate-400">
                              {[
                                contact.primary_address_city, 
                                getStateName(contact.primary_address_state, contact.primary_address_country), 
                                contact.primary_address_postal_code, 
                                getCountryName(contact.primary_address_country)
                              ].filter(Boolean).join(', ')}
                            </p>
                          </>
                        ) : (
                          <p className="font-medium"></p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-4 md:space-y-8">
                <div className="p-4 md:p-6 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-xl border border-blue-100 dark:border-blue-800">
                  <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-4">{t('contacts.quickOverview')}</h3>
                  <div className="space-y-4">
              <div>
                <p className="text-xs text-slate-500 dark:text-slate-400">{t('fields.contactSince')}</p>
                      <p className="text-slate-900 dark:text-slate-100 font-medium mt-1">
                        {formatDate(contact.created_at || contact.created_date)}
                      </p>
                    </div>

                    {/* Source & Channel Section */}
                    <div className="pt-4 border-t border-blue-200 dark:border-blue-700">
                      <p className="text-xs text-slate-500 dark:text-slate-400 font-semibold mb-3">{t('deals.sourceAndChannel')}</p>
                      
                      <div className="space-y-3">
                        {/* Contact Source */}
                        <div>
                          <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">{t('fields.source')}</p>
                          {/* 
                            Source can be edited if:
                            - No source set yet, OR
                            - User is admin AND contact was NOT referred (or admin can always override)
                            Blocked if:
                            - Contact was referred (referredBy exists) - source is locked to 'Customer Referral'
                            - Source already set (unless admin)
                          */}
                          {(() => {
                            const isReferred = !!referredBy;
                            const sourceNotSet = !contact.contacts_source || contact.contacts_source === 'Unselected';
                            const canEditSource = isAdmin || (sourceNotSet && !isReferred);
                            
                            if (canEditSource) {
                              return (
                                <Select
                                  value={contact.contacts_source || 'Unselected'}
                                  onValueChange={async (value) => {
                                    try {
                                      await supabase
                                        .from('contacts')
                                        .update({ contacts_source: value })
                                        .eq('id', contact.id);
                                      await loadContact();
                                      toast.success('Contact source updated');
                                    } catch (error) {
                                      console.error('Error updating source:', error);
                                      toast.error('Error updating source');
                                    }
                                  }}
                                >
                                  <SelectTrigger className="w-full h-8 text-sm">
                                    <SelectValue placeholder="Select source..." />
                                  </SelectTrigger>
                                  <SelectContent className="bg-white dark:bg-slate-800 z-50">
                                    {SOURCE_TYPES.map((source) => (
                                      <SelectItem key={source.value} value={source.value}>
                                        {t(source.labelKey)}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              );
                            } else {
                              return (
                                <div className="flex items-center gap-2">
                                  <Badge variant="outline" className="capitalize">
                                    {contact.contacts_source || (isReferred ? t('referrals.sourceLabel') : 'N/A')}
                                  </Badge>
                                  <span className="text-xs text-slate-400" title={isReferred ? 'Contato indicado - origem bloqueada' : 'Origem definida - apenas admin pode alterar'}>🔒</span>
                                </div>
                              );
                            }
                          })()}
                        </div>

                        {/* Contact Channel */}
                        <div>
                          <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">{t('fields.channel')}</p>
                          <Select
                            value={contact.contacts_channel || 'Other'}
                            onValueChange={async (value) => {
                              try {
                                await supabase
                                  .from('contacts')
                                  .update({ contacts_channel: value })
                                  .eq('id', contact.id);
                                await loadContact();
                                toast.success('Contact channel updated');
                              } catch (error) {
                                console.error('Error updating channel:', error);
                                toast.error('Error updating channel');
                              }
                            }}
                          >
                            <SelectTrigger className="w-full h-8 text-sm">
                              <SelectValue placeholder="Select channel..." />
                            </SelectTrigger>
                            <SelectContent className="bg-white dark:bg-slate-800 z-50">
                              {CHANNEL_TYPES.map((channel) => (
                                <SelectItem key={channel.value} value={channel.value}>
                                  {t(channel.labelKey)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>

                    {/* Status & Classification Section */}
                    <div className="pt-4 border-t border-blue-200 dark:border-blue-700">
                      <p className="text-xs text-slate-500 dark:text-slate-400 font-semibold mb-3">{t('contacts.statusClassification').toUpperCase()}</p>
                      
                      <div className="space-y-3">
                        {/* Contact Type */}
                        {contact.contact_type && (
                          <div>
                            <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">{t('contactForm.contactType')}</p>
                            <Badge 
                              variant={
                                contact.contact_type === 'active_customer' ? 'default' :
                                contact.contact_type === 'prospect' ? 'secondary' : 
                                'outline'
                              }
                              className="capitalize"
                            >
                              {t(`contactTypes.${contact.contact_type}`)}
                            </Badge>
                          </div>
                        )}

                        {/* Customer Status - só mostra se for customer */}
                        {contact.customer_status && (
                          contact.contact_type === 'active_customer' || 
                          contact.contact_type === 'past_customer'
                        ) && (
                          <div>
                            <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">{t('contactForm.customerStatus')}</p>
                            <Badge 
                              variant={
                                contact.customer_status === 'active' ? 'default' :
                                contact.customer_status === 'at_risk' ? 'destructive' : 
                                'secondary'
                              }
                              className="capitalize"
                            >
                              {t(`customerStatus.${contact.customer_status}`)}
                            </Badge>
                          </div>
                        )}

                        {/* Lifecycle Status */}
                        {contact.contact_lifecycle_status && (
                          <div>
                            <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">{t('contacts.lifecycleStage')}</p>
                            <Badge variant="outline" className="capitalize">
                              {t(`lifecycleStatus.${contact.contact_lifecycle_status}`)}
                            </Badge>
                          </div>
                        )}

                        {/* Satisfaction Level */}
                        {contact.satisfaction_level && (
                          <div>
                            <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">{t('contacts.satisfaction')}</p>
                            <Badge 
                              className={
                                contact.satisfaction_level === 'promoter' 
                                  ? 'bg-green-100 text-green-800 border-green-300' :
                                contact.satisfaction_level === 'detractor'
                                  ? 'bg-red-100 text-red-800 border-red-300' :
                                'bg-yellow-100 text-yellow-800 border-yellow-300'
                              }
                            >
                              {contact.satisfaction_level === 'promoter' ? `😊 ${t('satisfactionLevel.promoter')}` :
                               contact.satisfaction_level === 'detractor' ? `😞 ${t('satisfactionLevel.detractor')}` :
                               `😐 ${t('satisfactionLevel.neutral')}`}
                            </Badge>
                          </div>
                        )}

                        {/* Verification Status */}
                        {contact.verification_status && (
                          <div>
                            <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">{t('contacts.verification')}</p>
                            <Badge 
                              variant={
                                contact.verification_status === 'verified' ? 'default' :
                                contact.verification_status === 'invalid' ? 'destructive' :
                                'secondary'
                              }
                              className="gap-1"
                            >
                              {contact.verification_status === 'verified' ? (
                                <><CheckCircle className="w-3 h-3" /> {t('verificationStatus.verified')}</>
                              ) : contact.verification_status === 'invalid' ? (
                                <><XCircle className="w-3 h-3" /> {t('verificationStatus.invalid')}</>
                              ) : (
                                <><AlertCircle className="w-3 h-3" /> {t('verificationStatus.unverified')}</>
                              )}
                            </Badge>
                          </div>
                        )}

                        {/* Preferences - Do Not Call / Do Not Message */}
                        {(contact.do_not_call || contact.do_not_message) && (
                          <div>
                            <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">{t('contacts.preferences')}</p>
                            <div className="flex flex-wrap gap-1">
                              {contact.do_not_call && (
                                <Badge variant="destructive" className="text-xs gap-1">
                                  <PhoneOff className="w-3 h-3" />
                                  {t('contacts.doNotCall')}
                                </Badge>
                              )}
                              {contact.do_not_message && (
                                <Badge variant="destructive" className="text-xs gap-1">
                                  <MessageCircleOff className="w-3 h-3" />
                                  {t('contacts.doNotMessage')}
                                </Badge>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {contact.created_by_display_name && (
                      <div>
                        <p className="text-xs text-slate-500 dark:text-slate-400">{t('contacts.createdBy')}</p>
                        <p className="text-slate-900 dark:text-slate-100 font-medium mt-1">
                          {contact.created_by_display_name}
                        </p>
                        {contact.created_by_email && (
                          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                            {contact.created_by_email}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Reactivation Card - only shows if contact has reactivation status */}
                <ReactivationCard contact={contact} />

                <div>
                  <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">{t('contacts.quickActions')}</h3>
                  <div className="space-y-2">
                    <Button
                      variant="outline"
                      onClick={() => setIsEditing(true)}
                      className="w-full justify-start border-slate-300"
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      {t('contacts.editInformation')}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === "deals" && (
            <DealsTab contactId={contactId} />
          )}

          {activeTab === "insights" && (
            <ContactInsightsPanel contactId={contactId} />
          )}

          {activeTab === "cases-services" && (
            <CasesServicesTab contactId={contactId} />
          )}

          {activeTab === "activities" && (
            <ContactActivityTimeline
              contactId={contactId}
              contactPhone={getFullPhoneE164(contact?.primary_phone, contact?.primary_phone_country_code)}
              contactName={`${contact?.first_name || ''} ${contact?.last_name || ''}`.trim()}
              isEditable
            />
          )}

          {activeTab === "documents" && (
            <DocumentsTab contactId={contactId} />
          )}

          {activeTab === "financeiro" && (
            <FinanceiroTab 
              contactId={contactId} 
              contactName={contact?.first_name ? `${contact.first_name} ${contact.last_name || ''}`.trim() : undefined}
              contactPhone={contact?.primary_phone}
            />
          )}

          {activeTab === "family" && (
            <FamilyRelationshipsTab contactId={contactId} />
          )}

          {activeTab === "referrals" && (
            <ReferralsTab contactId={contactId} />
          )}
        </div>

        {/* Dialog de atribuição removido — Consultor/SDR são definidos no deal, não no contato */}

        {/* Quick Action Modals */}
        
        {/* Deal Form Modal */}
        <Dialog open={showDealForm} onOpenChange={setShowDealForm}>
          <DialogContent className="w-[calc(100vw-1.5rem)] sm:w-[calc(100vw-2rem)] max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{t('contacts.createDealFor', { name: getFullName() })}</DialogTitle>
            </DialogHeader>
            <DealForm
              initialContactId={contactId}
              onSubmit={handleCreateDeal}
              onCancel={() => setShowDealForm(false)}
              isProcessing={isCreatingDeal}
            />
          </DialogContent>
        </Dialog>

        {/* Task Form Modal */}
        <Dialog open={showTaskForm} onOpenChange={setShowTaskForm}>
          <DialogContent className="w-[calc(100vw-1.5rem)] sm:w-[calc(100vw-2rem)] max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{t('contacts.createTaskFor', { name: getFullName() })}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleTaskSubmit(handleCreateTask)} className="space-y-4">
              <div>
                <Label>{t('tasks.title')} *</Label>
                <Input {...register('title', { required: true })} placeholder={t('tasks.taskName')} />
              </div>
              <div>
                <Label>{t('tasks.description')}</Label>
                <Textarea {...register('description')} placeholder={t('tasks.describeTask')} rows={4} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{t('tasks.priority')}</Label>
                  <Select onValueChange={(value) => setTaskValue('priority', value)} defaultValue="medium">
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">{t('priority.low')}</SelectItem>
                      <SelectItem value="medium">{t('priority.medium')}</SelectItem>
                      <SelectItem value="high">{t('priority.high')}</SelectItem>
                      <SelectItem value="urgent">{t('priority.urgent')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{t('tasks.dueDate')}</Label>
                  <Input type="date" {...register('due_date')} />
                </div>
              </div>
              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" onClick={() => setShowTaskForm(false)}>{t('common.cancel')}</Button>
                <Button type="submit" disabled={createTask?.isPending}>
                  {createTask?.isPending ? t('tasks.creating') : t('tasks.createTask')}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Schedule Call Modal */}
        <ScheduleCallModal
          open={showScheduleCall}
          onClose={() => setShowScheduleCall(false)}
          onSchedule={async () => {
            setShowScheduleCall(false);
            toast.success(t('calls.meetingScheduled', 'Reunião agendada com sucesso'));
          }}
          contactId={contactId}
          dealId={activeDealId}
          prefilledPhone={contact?.primary_phone}
        />

        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('contacts.deleteContact')}</AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div>
                  <p>{t('contacts.deleteConfirmation')}</p>
                  
                  {loadingDeals ? (
                    <div className="flex items-center gap-2 mt-4">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Carregando deals...</span>
                    </div>
                  ) : contactDeals.length > 0 && (
                    <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-amber-800 font-medium">
                          Este contato possui {contactDeals.length} deal{contactDeals.length > 1 ? 's' : ''} associado{contactDeals.length > 1 ? 's' : ''}:
                        </p>
                        <button
                          type="button"
                          onClick={() => {
                            if (selectedDealsToDelete.length === contactDeals.length) {
                              setSelectedDealsToDelete([]);
                            } else {
                              setSelectedDealsToDelete(contactDeals.map(d => d.id));
                            }
                          }}
                          className="text-xs text-amber-700 hover:text-amber-900 underline"
                        >
                          {selectedDealsToDelete.length === contactDeals.length ? 'Desmarcar todos' : 'Selecionar todos'}
                        </button>
                      </div>
                      <ul className="text-sm text-amber-700 mb-3 max-h-40 overflow-y-auto space-y-2">
                        {contactDeals.map(deal => (
                          <li key={deal.id} className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              id={`deal-${deal.id}`}
                              checked={selectedDealsToDelete.includes(deal.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedDealsToDelete(prev => [...prev, deal.id]);
                                } else {
                                  setSelectedDealsToDelete(prev => prev.filter(id => id !== deal.id));
                                }
                              }}
                              className="w-4 h-4 rounded border-amber-400"
                            />
                      <label htmlFor={`deal-${deal.id}`} className="cursor-pointer flex-1">
                        {deal.name || 'Deal sem nome'} ({deal.stage}) - {new Intl.NumberFormat('en-US', { 
                          style: 'currency', 
                          currency: deal.currency || 'USD' 
                        }).format(deal.total_amount || 0)}
                      </label>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDeleteContact} 
                className="bg-destructive hover:bg-destructive/90"
                disabled={loadingDeals}
              >
                {t('common.delete')}
                {selectedDealsToDelete.length > 0 && ` (+ ${selectedDealsToDelete.length} deal${selectedDealsToDelete.length > 1 ? 's' : ''})`}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      {showWhatsAppSheet && contact?.id && (
        <DrawerChatLayout onClose={() => setShowWhatsAppSheet(false)}>
          <ChatCore
            contactId={contactId}
            contactPhone={getFullPhoneE164(contact.primary_phone, contact.primary_phone_country_code)}
            showConversationSelector
            header={
              <DealChatHeader
                contactName={getFullName()}
                phoneNumber={formatPhoneNumber(contact.primary_phone, contact.primary_phone_country_code)}
                isConnected
                onClose={() => setShowWhatsAppSheet(false)}
                onCall={() => {
                  const phone = getFullPhoneE164(contact.primary_phone, contact.primary_phone_country_code);
                  if (phone) window.dispatchEvent(new CustomEvent('initiate-call', { detail: { phoneNumber: phone } }));
                }}
              />
            }
          />
        </DrawerChatLayout>
      )}
    </div>
  );
}